/**
 * Truthful classification of the Dashboard's cloud game-list load error
 * (#3123).
 *
 * ## The lie this kills
 *
 * When the Cloud Foundry view calls `game.list@1` and it fails with a
 * cloud auth rejection, the furnace throws a `-32001 auth_failure` whose
 * message is **"Cloud operation is not available for this account"**
 * (see `packages/crucible/src/runtime/cloud-method-authorization.ts`).
 * The Dashboard used to render that verbatim under a red "Failed to load
 * games" — condemning the ACCOUNT.
 *
 * #3123 root-caused the real cause as furnace-image **rollout latency**:
 * the eligibility fix (#3110) is merged and IS the target image, but
 * stale furnaces on pre-`c2edd86` images reject until the nightly
 * reconcile sweeps them. So the state is **transient / pending-rollout**,
 * not an account entitlement problem — a retry (or simply waiting a beat)
 * clears it once the furnace rolls.
 *
 * ## Why match on the exact message — and ONLY that
 *
 * The numeric `-32001` code and any `category` are both LOST on the way
 * to the renderer: the furnace's `errorResponseFromException` emits the
 * `-32001` envelope with **no `data`**, so the client's `fromJsonRpcError`
 * (browser) and `flattenIpcError` → `toSerialisedError` (desktop IPC) both
 * fall through to `category: 'unknown'`, and the numeric code is dropped
 * (`extractSystemErrorFields` only keeps a *string* `code`). The only
 * signal that survives verbatim across BOTH transports is the error
 * **message**.
 *
 * We therefore match the ONE exact phrase provably tied to the -32001
 * cohort-eligibility / rollout condition, and nothing else. In
 * particular we do NOT trigger on `category === 'auth'` nor on the
 * generic "requires an authenticated identity" text: the browser client
 * emits `CrucibleError` with `category: 'auth'` for GENUINE
 * credential-acquisition failures and `4401`/`4402` auth-close rejections
 * (see `BrowserCrucibleClient.onAcquireFailure` / `midHandshakeError`).
 * Because the Dashboard routes EVERY list/preferences error through this
 * classifier, a broader predicate would disguise a real sign-in/session
 * failure as "give it a moment, then Retry" — worse than the original
 * bug, since it invites a futile retry loop and hides the actual auth
 * problem.
 *
 * ## This is an interim workaround
 *
 * String-matching a free-text message to make a semantic decision is a
 * smell (see `.claude/rules/regex-not-for-semantic-classification.md`).
 * The DURABLE fix is a structured furnace-side `error.data`
 * (`reason` / `category`) on the `-32001`, classified on THAT field. This
 * narrow, single-phrase match is the interim CLIENT workaround for the
 * exact message the furnace emits today; the structured-error follow-up
 * is tracked on #3123.
 */

import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';

/** Heading for the transient cloud-auth error state. */
export const TRANSIENT_CLOUD_AUTH_TITLE = "Couldn't reach your cloud workspace";

/**
 * Body copy for the transient cloud-auth error state. Frames the failure
 * as a brief, self-healing rollout blip and points at the Retry
 * affordance — it must NOT assert account-level unavailability.
 */
export const TRANSIENT_CLOUD_AUTH_DETAIL =
  'This can happen briefly after an update while your workspace finishes rolling out. Give it a moment, then try again.';

/**
 * The ONE lower-cased phrase provably tied to the -32001 cohort-eligibility
 * / furnace-image-rollout condition #3123 is about
 * (`cloud-method-authorization.ts` — the `resolveCloudVmEligibility`
 * rejection). This is the exact text the furnace emits today.
 *
 * Deliberately the ONLY marker:
 *   - we do NOT match the jsonrpc-channel §3.9 gate
 *     (`"Method not allowed for this token: …"`) — a distinct,
 *     account-neutral, token-scoped rejection with its own recovery;
 *   - we do NOT match `"requires an authenticated identity"` — a genuine
 *     missing-identity / session condition, not stale-furnace rollout.
 */
const ACCOUNT_ROLLOUT_MESSAGE_MARKER = 'cloud operation is not available for this account';

/**
 * True ONLY when `error` is the account-condemning cloud `auth_failure`
 * (`-32001`, exact phrase {@link ACCOUNT_ROLLOUT_MESSAGE_MARKER}) that
 * #3123 root-caused as transient furnace-image rollout latency. Such an
 * error is presented as a retry-able transient state.
 *
 * Every OTHER error — including genuine `category: 'auth'`
 * credential-acquisition failures and `4401`/`4402` auth closes — returns
 * `false` and keeps the normal "Failed to load games" surface with its
 * own message, so a real sign-in/session failure is never disguised as a
 * transient blip.
 *
 * Robust to the shapes the error arrives in: a `CrucibleError`
 * (crucible-client), a sentinel-encoded IPC `Error`, or a plain `Error` —
 * `unflattenIpcError` normalises all three and decodes the inner message.
 */
export function isTransientCloudAuthError(error: unknown): boolean {
  if (error === null || error === undefined) return false;
  const message = (unflattenIpcError(error).message ?? '').toLowerCase();
  return message.includes(ACCOUNT_ROLLOUT_MESSAGE_MARKER);
}
